import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { tenantDocTypesApi } from "../services/apiClient";
import {
  DOC_TYPES,
  defaultEnabledByCode,
  getDocTypesForProofForm,
  isDocTypeEnabled as isDocTypeEnabledFromMap,
} from "./docTypes";

const DocTypesEnabledContext = createContext(null);

export function DocTypesEnabledProvider({ children, sessionReadOnly = false, tenantId = null }) {
  const [enabledByCode, setEnabledByCode] = useState(defaultEnabledByCode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tenantDocTypesApi.get();
      const raw = data?.enabled && typeof data.enabled === "object" ? data.enabled : {};
      const next = defaultEnabledByCode();
      for (const d of DOC_TYPES) {
        if (Object.prototype.hasOwnProperty.call(raw, d.code)) {
          next[d.code] = !!raw[d.code];
        }
      }
      setEnabledByCode(next);
    } catch (e) {
      setError(e?.message || "Impossible de charger les types de documents.");
      setEnabledByCode(defaultEnabledByCode());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tenantId == null) {
      setEnabledByCode(defaultEnabledByCode());
      setLoading(false);
      setError(null);
      return;
    }
    refresh();
  }, [tenantId, refresh]);

  const setDocTypeEnabled = useCallback(
    async (code, enabled) => {
      if (sessionReadOnly || tenantId == null) return false;

      const next = { ...enabledByCode, [code]: enabled };
      const activeCount = DOC_TYPES.filter(d => next[d.code]).length;
      if (activeCount < 1) return false;

      const prev = { ...enabledByCode };
      setEnabledByCode(next);
      try {
        await tenantDocTypesApi.update(next);
      } catch (e) {
        setEnabledByCode(prev);
        throw e;
      }
      return true;
    },
    [enabledByCode, sessionReadOnly, tenantId]
  );

  const value = useMemo(
    () => ({
      enabledByCode,
      loading,
      error,
      refresh,
      setDocTypeEnabled,
      sessionReadOnly,
      tenantId,
      activeDocTypes: getDocTypesForProofForm(enabledByCode),
      isDocTypeEnabled: code => isDocTypeEnabledFromMap(code, enabledByCode),
    }),
    [enabledByCode, loading, error, refresh, setDocTypeEnabled, sessionReadOnly, tenantId]
  );

  return <DocTypesEnabledContext.Provider value={value}>{children}</DocTypesEnabledContext.Provider>;
}

export function useDocTypesEnabled() {
  const ctx = useContext(DocTypesEnabledContext);
  if (!ctx) {
    throw new Error("useDocTypesEnabled doit être utilisé sous DocTypesEnabledProvider.");
  }
  return ctx;
}
